"use client";
/* eslint-disable @next/next/no-img-element */

import { useEffect, useRef, useState, useSyncExternalStore } from "react";

interface DeferredDecorativeVideoProps {
	src: string;
	poster: string;
	active: boolean;
	posterVisible?: boolean;
	priority?: boolean;
	className?: string;
	videoClassName?: string;
}

function usePrefersReducedMotion() {
	const [reducedMotion, setReducedMotion] = useState(
		() => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
	);

	useEffect(() => {
		const media = window.matchMedia?.("(prefers-reduced-motion: reduce)");
		if (!media) return;
		const update = () => setReducedMotion(media.matches);
		update();
		media.addEventListener("change", update);
		return () => media.removeEventListener("change", update);
	}, []);

	return reducedMotion;
}

function useDocumentVisible() {
	const [visible, setVisible] = useState(
		() => typeof document === "undefined" || document.visibilityState === "visible",
	);

	useEffect(() => {
		const update = () => setVisible(document.visibilityState === "visible");
		document.addEventListener("visibilitychange", update);
		return () => document.removeEventListener("visibilitychange", update);
	}, []);

	return visible;
}

type DataSavingConnection = Partial<Pick<EventTarget, "addEventListener" | "removeEventListener">> & {
	saveData?: boolean;
};

function getConnection() {
	return typeof navigator === "undefined"
		? undefined
		: (navigator as Navigator & { connection?: DataSavingConnection }).connection;
}

function subscribeToDataSaving(onChange: () => void) {
	const connection = getConnection();
	connection?.addEventListener?.("change", onChange);
	return () => connection?.removeEventListener?.("change", onChange);
}

function getDataSavingSnapshot() {
	return getConnection()?.saveData === true;
}

// Keep server HTML static until the browser's network preference is available.
function getServerDataSavingSnapshot() {
	return true;
}

/**
 * Decorative Stream footage that stays a static poster until its parent opts in.
 * The video node is removed when inactive so hidden App Router views cannot keep
 * downloading or playing media in the background.
 */
export function DeferredDecorativeVideo({
	src,
	poster,
	active,
	posterVisible = true,
	priority = false,
	className,
	videoClassName,
}: DeferredDecorativeVideoProps) {
	const [failed, setFailed] = useState(false);
	const videoRef = useRef<HTMLVideoElement>(null);
	const reducedMotion = usePrefersReducedMotion();
	const documentVisible = useDocumentVisible();
	const saveData = useSyncExternalStore(
		subscribeToDataSaving,
		getDataSavingSnapshot,
		getServerDataSavingSnapshot,
	);
	const shouldMountVideo = active && documentVisible && !reducedMotion && !saveData && !failed;

	useEffect(() => {
		const video = videoRef.current;
		if (!video) return;
		if (shouldMountVideo) {
			void video.play().catch(() => undefined);
		}
		return () => video.pause();
	}, [shouldMountVideo]);

	return (
		<div aria-hidden className={className}>
			{/* Stream posters are remote runtime URLs, so native img avoids an optimizer allow-list. */}
			{posterVisible ? (
				<img
					data-testid="decorative-poster"
					className="absolute inset-0 h-full w-full object-cover"
					src={poster}
					loading={priority ? "eager" : "lazy"}
					fetchPriority={priority ? "high" : undefined}
					alt=""
				/>
			) : null}
			{shouldMountVideo ? (
				<video
					data-testid="decorative-video"
					ref={videoRef}
					className={videoClassName ?? "absolute inset-0 h-full w-full object-cover"}
					src={src}
					preload="none"
					autoPlay
					loop
					muted
					playsInline
					onError={() => setFailed(true)}
				/>
			) : null}
		</div>
	);
}

function useFinePointer() {
	const [enabled, setEnabled] = useState(false);

	useEffect(() => {
		const media = window.matchMedia?.("(hover: hover) and (pointer: fine)");
		if (!media) return;
		const update = () => setEnabled(media.matches);
		update();
		media.addEventListener("change", update);
		return () => media.removeEventListener("change", update);
	}, []);

	return enabled;
}

function useMinimumViewportWidth(minViewportWidth?: number) {
	const [matches, setMatches] = useState(() => minViewportWidth == null);

	useEffect(() => {
		if (minViewportWidth == null) return;
		const media = window.matchMedia?.(`(min-width: ${minViewportWidth}px)`);
		if (!media) return;
		const update = () => setMatches(media.matches);
		update();
		media.addEventListener("change", update);
		return () => media.removeEventListener("change", update);
	}, [minViewportWidth]);

	return matches;
}

interface ViewportDeferredDecorativeVideoProps {
	src: string;
	poster: string;
	className?: string;
	videoClassName?: string;
	/** Omit all Stream media from CSS-hidden mobile regions below this width. */
	minViewportWidth?: number;
	/** Render the above-the-fold poster in the initial HTML; video stays deferred. */
	priority?: boolean;
}

/**
 * Client-only viewport gate for a server-rendered decorative-media region.
 * Touch layouts keep a static poster. CSS-hidden regions can omit all media
 * with minViewportWidth; critical posters can opt into server rendering.
 */
export function ViewportDeferredDecorativeVideo({
	src,
	poster,
	className,
	videoClassName,
	minViewportWidth,
	priority = false,
}: ViewportDeferredDecorativeVideoProps) {
	const ref = useRef<HTMLDivElement>(null);
	const finePointer = useFinePointer();
	const viewportAllowed = useMinimumViewportWidth(minViewportWidth);
	const [inViewport, setInViewport] = useState(false);

	useEffect(() => {
		if (!viewportAllowed || !ref.current || !window.IntersectionObserver) return;
		const observer = new IntersectionObserver(([entry]) => setInViewport(entry?.isIntersecting ?? false), {
			rootMargin: "120px 0px",
		});
		observer.observe(ref.current);
		return () => observer.disconnect();
	}, [viewportAllowed]);

	return (
		<div ref={ref} className={className}>
			<DeferredDecorativeVideo
				src={src}
				poster={poster}
				posterVisible={viewportAllowed && (priority || inViewport)}
				priority={priority}
				active={viewportAllowed && finePointer && inViewport}
				videoClassName={videoClassName}
			/>
		</div>
	);
}
